import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';
import { getNearestElement } from '../utils/getNearestElement.js';

import s from './Input.module.css';
import { useInput } from './InputRoot.js';
import { cva } from 'class-variance-authority';

const inputGroupVariants = cva(s.group, {
  variants: {
    variant: {
      default: s.defaultVariant,
      error: s.errorVariant,
    },
  },
});

export type InputGroupProps = ComponentPropsWithRef<'div'> & {
  className?: string;
  children?: ReactNode;
};

export const InputGroup = ({
  children,
  className,
  onClick,
  ...attrs
}: InputGroupProps) => {
  const { variant } = useInput();

  const handleInteraction: ComponentPropsWithRef<'div'>['onClick'] = (e) => {
    // This handleInteraction function is used to handle clicks on the input group.
    // It checks if the clicked element is an interactive element (like a button or link).
    // If it is not, it focuses the first input element inside the group.
    // This is useful for accessibility, as it allows users to click anywhere in the group to focus the input.
    // For keyboard access, we reply on the inputs to handle their own key events, do we use a no-op function here.
    const element = e.target as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    const interactiveElements = [
      'a',
      'button',
      'input',
      'textarea',
      'select',
      'label',
    ];

    const isInteractive = interactiveElements.includes(tagName);

    const inputs = e.currentTarget.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement
    >('input, textarea');

    const selectedInput = getNearestElement(e.nativeEvent, inputs);

    if (selectedInput && !isInteractive) {
      selectedInput.focus();
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <div
      className={clsx(inputGroupVariants({ variant }), className)}
      onClick={handleInteraction}
      role="presentation"
      tabIndex={-1}
      {...attrs}
    >
      {children}
    </div>
  );
};
